import { HttpRequestMethodTypes, JSON_CONTENT_TYPE_HEADER } from 'types';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';

const API_BASE_URL = '/api/smartchat/conversations';

interface ConversationApiParams {
  jwt: string;
}

interface CreateConversationParams extends ConversationApiParams {
  title?: string;
  metadata?: Record<string, string>;
}

interface ListConversationsParams extends ConversationApiParams {
  limit?: number;
  cursor?: string;
}

interface GetConversationParams extends ConversationApiParams {
  conversationId: string;
}

interface UpdateConversationParams extends ConversationApiParams {
  conversationId: string;
  title?: string;
  metadata?: Record<string, string>;
}

interface DeleteConversationParams extends ConversationApiParams {
  conversationId: string;
}

export interface ConversationResponse {
  conversationId: string;
  title: string;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConversationResponse {
  conversationId: string;
  createdAt: string;
  title: string;
}

export interface ListConversationsResponse {
  conversations: ConversationResponse[];
  nextCursor: string;
  totalCount?: number;
}

/**
 * Creates a new conversation.
 */
export async function createConversation({
  jwt,
  title,
  metadata,
}: CreateConversationParams): Promise<CreateConversationResponse> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.POST,
    headers: {
      ...JSON_CONTENT_TYPE_HEADER,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ title, metadata }),
  };

  const response = await fetchWithRetry(API_BASE_URL, options);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to create conversation: ${response.status} - ${errorData.error}`);
  }

  return response.json();
}

/**
 * Lists conversations for the authenticated user with pagination support.
 */
export async function listConversations({
  jwt,
  limit = 10,
  cursor,
}: ListConversationsParams): Promise<ListConversationsResponse> {
  const url = new URL(API_BASE_URL, window.location.origin);
  url.searchParams.set('limit', limit.toString());
  if (cursor) {
    url.searchParams.set('cursor', cursor);
  }

  const options: FetchOptions = {
    method: HttpRequestMethodTypes.GET,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  const response = await fetchWithRetry(url.pathname + url.search, options);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to list conversations: ${response.status} - ${errorData.error}`);
  }

  return response.json();
}

/**
 * Gets a specific conversation by ID.
 */
export async function getConversation({
  jwt,
  conversationId,
}: GetConversationParams): Promise<ConversationResponse> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.GET,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  const response = await fetchWithRetry(`${API_BASE_URL}/${conversationId}`, options);

  if (response.status === 404) {
    throw new Error('Conversation not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to get conversation: ${response.status} - ${errorData.error}`);
  }

  return response.json();
}

/**
 * Updates an existing conversation.
 */
export async function updateConversation({
  jwt,
  conversationId,
  title,
  metadata,
}: UpdateConversationParams): Promise<ConversationResponse> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.PATCH,
    headers: {
      ...JSON_CONTENT_TYPE_HEADER,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ title, metadata }),
  };

  const response = await fetchWithRetry(`${API_BASE_URL}/${conversationId}`, options);

  if (response.status === 404) {
    throw new Error('Conversation not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to update conversation: ${response.status} - ${errorData.error}`);
  }

  return response.json();
}

/**
 * Gets all conversations by fetching all pages.
 */
export async function getAllConversations({
  jwt,
}: ConversationApiParams): Promise<ConversationResponse[]> {
  const allConversations: ConversationResponse[] = [];
  let cursor: string | undefined = undefined;

  do {
    const response = await listConversations({ jwt, limit: 50, cursor });
    allConversations.push(...response.conversations);
    cursor = response.nextCursor || undefined;
  } while (cursor);

  return allConversations;
}

/**
 * Deletes a specific conversation by ID.
 */
export async function deleteConversation({
  jwt,
  conversationId,
}: DeleteConversationParams): Promise<void> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.DELETE,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  const response = await fetchWithRetry(`${API_BASE_URL}/${conversationId}`, options);

  if (response.status === 404) {
    throw new Error('Conversation not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to delete conversation: ${response.status} - ${errorData.error}`);
  }

  // 204 No Content - successful deletion
}

/**
 * Deletes all conversations for the authenticated user.
 */
export async function deleteAllConversations({
  jwt,
}: ConversationApiParams): Promise<number> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.DELETE,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/delete-all`, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to delete all conversations: ${response.status} - ${errorData.error}`);
    }

    const data = await response.json();
    return data.deletedCount;
  } catch (error) {
    console.error('Error deleting all conversations:', error);
    console.log(error);
    throw error;
  }
}
